import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import RecurrenteCheckout from 'recurrente-checkout';
import { Button } from '@/components/ui/button';
import { Loader2Icon } from 'lucide-react';
import notifyHB from '@/config/notifyHB';
import { OnboardingStatus } from '@/entities/onboarding-status';
import { createPaymentCheckout } from '@/use-cases/create-payment-checkout';
import { verifyPayment } from '@/use-cases/verify-payment';
import { waitForPayment } from '@/use-cases/wait-for-payment';
import routeConfig from '@/config/routeConfig';
import { isOnboardingConflict } from '@/utils/is-onboarding-conflict';
import PaymentProductPicker from './PaymentProductPicker';
import { decidePaymentAction } from './payment-resume';
import styles from './payment-step.module.scss';

interface PaymentStepProps {
  nextStep: () => void;
  prevStep: () => void;
  canGoBack: boolean;
  selectedProductId: string | undefined;
  // Re-reads the backend status; null when it is unavailable.
  refreshStatus: () => Promise<OnboardingStatus | null>;
  // Called when the backend answers 409 (e.g. ALREADY_PAID).
  onConflict: () => void;
}

const PaymentStep: React.FC<PaymentStepProps> = ({
  nextStep,
  prevStep,
  canGoBack,
  selectedProductId,
  refreshStatus,
  onConflict,
}) => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  // Set when no plan is known (resumed in another browser): ask for one.
  const [needsProduct, setNeedsProduct] = useState(false);
  const [hasError, setHasError] = useState(false);
  const checkoutIdRef = useRef<string | null>(null);
  const loadedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    run(initCheckout);
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const advance = () => {
    if (mountedRef.current) nextStep();
  };

  /** Runs a payment action; every failure ends in a visible retry. */
  const run = async (action: () => Promise<void>) => {
    setHasError(false);
    setIsLoading(true);
    try {
      await action();
    } catch (error) {
      if (isOnboardingConflict(error)) {
        onConflict();
        return;
      }
      notifyHB('request-signature', 'PaymentStep', error);
      if (mountedRef.current) setHasError(true);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  };

  const openCheckout = (checkoutId: string, url: string) => {
    checkoutIdRef.current = checkoutId;
    localStorage.setItem('checkout-id', checkoutId);
    localStorage.setItem('checkout-url', url);
    setCheckoutUrl(url);
  };

  const createCheckout = async (productId: string | null) => {
    if (!productId) {
      setNeedsProduct(true);
      return;
    }
    const ulid = localStorage.getItem('onboarding-ulid') ?? '';
    const successUrl = `${window.location.origin}${routeConfig.requestSignature}`;
    const failureUrl = `${window.location.origin}${routeConfig.paymentFailure}`;
    const checkout = await createPaymentCheckout(
      ulid,
      productId,
      successUrl,
      failureUrl,
    );
    openCheckout(checkout.id, checkout.checkout_url);
  };

  // Used only when the status endpoint cannot be reached.
  const initCheckoutFromStorage = async () => {
    if (localStorage.getItem('onboarding-payment-method') === 'promo') {
      advance();
      return;
    }
    const existingCheckoutId = localStorage.getItem('checkout-id');
    const existingCheckoutUrl = localStorage.getItem('checkout-url');

    if (existingCheckoutId) {
      const verification = await verifyPayment(existingCheckoutId);
      if (verification.status === 'paid') {
        advance();
        return;
      }
      if (existingCheckoutUrl) {
        openCheckout(existingCheckoutId, existingCheckoutUrl);
        return;
      }
    }

    await createCheckout(selectedProductId || null);
  };

  const initCheckout = async () => {
    const status = await refreshStatus();
    if (!status) {
      await initCheckoutFromStorage();
      return;
    }

    const action = decidePaymentAction(status, selectedProductId);
    if (action.kind === 'skip') {
      advance();
      return;
    }

    if (action.kind === 'poll') {
      if (await waitForPayment(action.checkoutId)) {
        advance();
        return;
      }
      // Still pending: reopen the same checkout, from the backend or from
      // this browser, before creating another one.
      const storedCheckoutUrl =
        localStorage.getItem('checkout-id') === action.checkoutId
          ? localStorage.getItem('checkout-url')
          : null;
      const pendingCheckoutUrl = action.checkoutUrl ?? storedCheckoutUrl;
      if (pendingCheckoutUrl) {
        openCheckout(action.checkoutId, pendingCheckoutUrl);
        return;
      }
    }

    await createCheckout(action.productId);
  };

  const handleProductPicked = (productId: string) => {
    setNeedsProduct(false);
    run(() => createCheckout(productId));
  };

  useEffect(() => {
    if (!checkoutUrl || loadedRef.current) return;
    loadedRef.current = true;
    try {
      RecurrenteCheckout.load({
        url: checkoutUrl,
        onSuccess: async () => {
          const checkoutId =
            checkoutIdRef.current ?? localStorage.getItem('checkout-id') ?? '';
          try {
            const verification = await verifyPayment(checkoutId);
            if (verification.status === 'paid') {
              nextStep();
            }
          } catch (error) {
            notifyHB('request-signature', 'PaymentStep', error);
          }
        },
        onFailure: () => {
          history.push(routeConfig.paymentFailure);
        },
        onPaymentInProgress: () => setIsLoading(true),
      });
    } catch (error) {
      loadedRef.current = false;
      setCheckoutUrl(null);
      notifyHB('request-signature', 'PaymentStep', error);
      setHasError(true);
    }
  }, [checkoutUrl]);

  return (
    <div className={styles['payment-step']}>
      {isLoading && (
        <div className={styles['loading']}>
          <Loader2Icon className="animate-spin" />
        </div>
      )}
      {hasError && !isLoading && (
        <div className={styles['error']} role="alert">
          <span>
            No pudimos preparar tu pago. Intenta de nuevo; si el problema
            persiste escríbenos a{' '}
            <a href="mailto:contacto@cybersign.gt">contacto@cybersign.gt</a>.
          </span>
          <Button variant="outline" onClick={() => run(initCheckout)}>
            Reintentar
          </Button>
        </div>
      )}
      {needsProduct && !isLoading && !hasError && (
        <PaymentProductPicker onSelect={handleProductPicked} />
      )}
      <div id="recurrente-checkout-container" />
      {canGoBack && (
        <Button variant="secondary" onClick={prevStep} disabled={isLoading}>
          Regresar
        </Button>
      )}
    </div>
  );
};

export default PaymentStep;
